import type {
  AddressMeterPointDetails,
  CreateEnrolmentRequest,
  CreditCheckRequest,
} from "@/api/model"
import { ElecProductType } from "@/api/model/elecProductType"
import type { EnrolmentAddressRequest } from "@/api/model/enrolmentAddressRequest"
import { EstimatedUsageType } from "@/api/model/estimatedUsageType"
import { FuelType } from "@/api/model/fuelType"
import type { QuotesResponse } from "@/api/model/quotesResponse"
import { TariffType } from "@/api/model/tariffType"
import { VARIABLE_TARIFF_RATE_TYPE } from "@/constants/tariff"
import { DateFormats } from "@/lib/dateHelper"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"

import type {
  EnrolmentAddress,
  EnrolmentEv,
  EnrolmentExtras,
  EnrolmentPayment,
  EnrolmentPersonalDetails,
  EnrolmentProperty,
  EnrolmentTariff,
} from "@/views/enrolment/useEnrolment"

dayjs.extend(customParseFormat)

function formatApiDate(date?: string | null): string | null {
  return date ? dayjs(date, DateFormats.default).format("YYYY-MM-DD") : null
}

export function mapToHistoricAddress(
  address: AddressMeterPointDetails,
): EnrolmentAddressRequest {
  return {
    address1: address.address.addressLine1,
    address2: address.address.addressLine2 ?? null,
    address3: address.address.addressLine3 ?? null,
    countryCode: address.address.countryCode,
    postcode: address.address.postcode,
  }
}

export const MPAN_EMPTY_MESSAGE = "Please enter your MPAN to continue"
export const MPAN_ERROR_MESSAGE =
  "Your MPAN should be 13 digits. You'll find it on a recent energy bill."
export const MPRN_ERROR_MESSAGE =
  "Your MPRN should be 6 - 10 digits. You'll find it on a recent energy bill."
export const MPRN_EMPTY_MESSAGE_GAS =
  "Please enter your MPRN to get a gas quote"

export const usageMinMax = {
  gas: { min: 1000, max: 70000 },
  electricity: { min: 200, max: 30000 },
}

export function resolveKwh(
  isCustom: boolean,
  customValue?: number,
  ofgemValue?: number,
): number | undefined {
  if (isCustom) return customValue ? Number(customValue) : undefined
  return customValue ?? ofgemValue
}

export function isElecMultirate(type?: ElecProductType): boolean {
  return type === ElecProductType.Eco7 || type === ElecProductType.TOU
}

export function findSoFlexQuote(
  quotes?: QuotesResponse[] | null,
): QuotesResponse | null {
  return (
    quotes?.find(
      (quote) =>
        quote.rate === VARIABLE_TARIFF_RATE_TYPE &&
        quote.tariffType === TariffType.Standard,
    ) ?? null
  )
}

export function formatLargeNumber(value: number): string {
  return value.toLocaleString("en-GB")
}

export const houseSizeLabel: Record<string, string> = {
  [EstimatedUsageType.LOW]: "1 to 2 bedrooms (low energy use)",
  [EstimatedUsageType.MEDIUM]: "2 to 4 bedrooms (medium energy use)",
  [EstimatedUsageType.HIGH]: "4+ bedrooms (high energy use)",
  [EstimatedUsageType.EXACT_AMOUNT]: "Custom usage",
}

const fuelTypeLabel: Record<string, string> = {
  [FuelType.DUAL_FUEL]: "Electricity and gas",
  [FuelType.ELECTRICITY_ONLY]: "Electricity",
  [FuelType.GAS_ONLY]: "Gas",
}

export function buildPropertyReviewText(property: {
  selectedFuelType?: FuelType
  usageEstimateType?: EstimatedUsageType
  electricityKwh?: number
  gasKwh?: number
}): string[] {
  const isManual =
    property.usageEstimateType === EstimatedUsageType.EXACT_AMOUNT

  if (isManual) {
    const parts: string[] = []
    if (property.electricityKwh) {
      parts.push(
        `Electricity: ${formatLargeNumber(property.electricityKwh)} kWh`,
      )
    }
    if (property.gasKwh) {
      parts.push(`Gas: ${formatLargeNumber(property.gasKwh)} kWh`)
    }
    return parts.length ? [parts.join(" | ")] : []
  }

  const fuel = property.selectedFuelType
    ? fuelTypeLabel[property.selectedFuelType]
    : ""
  const size = houseSizeLabel[property.usageEstimateType ?? ""] ?? ""
  return [fuel, size].filter(Boolean)
}

export function buildCreditCheckRequest(params: {
  address: EnrolmentAddress
  personalDetails: EnrolmentPersonalDetails
  extras: EnrolmentExtras
  historicAddress: EnrolmentAddressRequest | null
}): CreditCheckRequest {
  const { address, personalDetails, extras, historicAddress } = params

  const toCreditCheckAddress = (a: {
    address1: string
    address2?: string | null
    address3?: string | null
    postcode: string
    countryCode: string
  }) => ({
    address1: a.address1,
    address2: a.address2 || undefined,
    address3: a.address3 || undefined,
    postcode: a.postcode,
    countryCode: a.countryCode || "GB",
  })

  return {
    contact: {
      title: personalDetails.title,
      firstName: personalDetails.firstName,
      lastName: personalDetails.lastName,
      email: personalDetails.email,
      phone: personalDetails.phone,
      birthDate: formatApiDate(personalDetails.dateOfBirth) ?? "",
    },
    addresses: [
      {
        current: true,
        address: toCreditCheckAddress({
          address1: address.addressLine1,
          address2: address.addressLine2,
          address3: address.addressLine3,
          postcode: address.postcode,
          countryCode: address.countryCode,
        }),
      },
      ...(historicAddress
        ? [{ current: false, address: toCreditCheckAddress(historicAddress) }]
        : []),
    ],
    brokerCode: extras.brokerCookie,
  }
}

export function buildEnrolmentRequest(params: {
  address: EnrolmentAddress
  personalDetails: EnrolmentPersonalDetails
  payment: EnrolmentPayment
  tariff: EnrolmentTariff
  property: EnrolmentProperty
  extras: EnrolmentExtras
  ev: EnrolmentEv
  historicAddress: EnrolmentAddressRequest | null
}): CreateEnrolmentRequest {
  const {
    address,
    personalDetails,
    payment,
    tariff,
    property,
    extras,
    ev,
    historicAddress,
  } = params

  const supplyAddress = {
    address1: address.addressLine1,
    address2: address.addressLine2 || null,
    address3: address.addressLine3 || null,
    postcode: address.postcode,
    countryCode: address.countryCode || "GB",
  }

  const birthdate = formatApiDate(personalDetails.dateOfBirth)

  const futureStartDate =
    formatApiDate(personalDetails.futureStartDate) ?? undefined

  const products: CreateEnrolmentRequest["products"] = []
  if (tariff.elecProductCode) {
    products.push({
      code: tariff.elecProductCode,
      meterPointIdentifiers: address.mpan ? [address.mpan] : [],
      type: "ELECTRICITY",
      consumptionEstimation: property.electricityKwh
        ? { consumption: property.electricityKwh, period: "year" }
        : undefined,
    })
  }
  if (tariff.gasProductCode) {
    products.push({
      code: tariff.gasProductCode,
      meterPointIdentifiers: address.mprn ? [address.mprn] : [],
      type: "GAS",
      consumptionEstimation: property.gasKwh
        ? { consumption: property.gasKwh, period: "year" }
        : undefined,
    })
  }

  return {
    type: "DOMESTIC",
    contacts: [
      {
        title: personalDetails.title,
        firstName: personalDetails.firstName,
        lastName: personalDetails.lastName,
        email: personalDetails.email,
        phone: personalDetails.phone,
        birthdate,
        isPrimaryContact: true,
        address: supplyAddress,
      },
    ],
    products,
    supplyAddress,
    paymentMethod: {
      bankAccountName: payment.bankAccountName ?? "",
      bankAccountNumber: payment.bankAccountNumber ?? "",
      bankAccountSortCode: (payment.bankAccountSortCode ?? "").replaceAll(
        "-",
        "",
      ),
      paymentDayOfMonth: payment.paymentDayOfMonth,
      paymentAmount: Math.round(payment.paymentAmount ?? 0),
      seasonalPayment: tariff.seasonalPayments ?? false,
    },
    marketingOptedIn: personalDetails.marketingOptIn,
    quoteId: tariff.quoteId ? String(tariff.quoteId) : null,
    priorityServiceRequired: personalDetails.vulnerableCustomer,
    voucher: extras.voucherCode,
    brokerCookie: extras.brokerCookie,
    thirdPartySwitchReference: extras.thirdPartySwitchReference,
    campaignReference: extras.campaignReference,
    durationMonths:
      tariff.tariffLengthInMonths == null
        ? null
        : String(tariff.tariffLengthInMonths),
    tariffName: tariff.tariffName,
    annualCost: tariff.costPerYearInPounds
      ? String(tariff.costPerYearInPounds)
      : null,
    historicSupplyAddresses: historicAddress ? [historicAddress] : null,
    evTariffMarketingConsent: ev.evTariffMarketingConsent ?? undefined,
    futureStartDate,
    prospectId: extras.creditCheck?.prospectId,
  }
}
